import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  accountRecoveryApi,
  RecoveryRequest,
} from '../../api/account-recovery';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';

/**
 * Locked-out customers the automatic checks could not settle.
 *
 * Deliberately a short list. A BVN that matched and a liveness check that
 * passed finishes on its own — a queue that exists to rubber-stamp two
 * agreeing checks gets rubber-stamped, and the customer cannot run their
 * business while it waits. What reaches a person here is the awkward
 * remainder, almost always a photo the model would not pass, where the
 * alternative is telling somebody who has proved who they are that they
 * cannot have their business back.
 *
 * ## What approving actually does
 *
 * It does not reset anything. It returns a one-time token, shown once and
 * never stored in the clear, which then has to be given to the customer
 * deliberately — on a channel the reviewer has satisfied themselves about.
 * That deliberate step is the point of a person being in this loop at all,
 * so the token is displayed with a warning rather than mailed automatically.
 */
const cell: React.CSSProperties = {
  padding: '12px 20px',
  borderBottom: '1px solid var(--neutral-200)',
  verticalAlign: 'top',
};

/** Never the whole thing. NDPR, and a reviewer does not need it. */
const maskIdentifier = (value: string) => {
  if (value.includes('@')) {
    const [name, domain] = value.split('@');
    return `${name.slice(0, 2)}${'•'.repeat(Math.max(1, name.length - 2))}@${domain}`;
  }
  return `${'•'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
};

export const AccountRecoveryQueue: React.FC = () => {
  usePage('Account recovery');
  const toast = useToast();
  const [requests, setRequests] = useState<RecoveryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [issuedToken, setIssuedToken] = useState<string | null>(null);

  const fetchPending = async () => {
    try {
      setLoading(true);
      setError(null);
      setRequests(await accountRecoveryApi.pending());
    } catch {
      setError('Failed to load the recovery queue.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPending();
  }, []);

  const decide = async (
    request: RecoveryRequest,
    decision: 'approve' | 'reject',
  ) => {
    try {
      setBusyId(request.id);
      const result = await accountRecoveryApi.review(
        request.id,
        decision,
        notes[request.id]?.trim() || undefined,
      );
      if (decision === 'approve' && result.recoveryToken) {
        setIssuedToken(result.recoveryToken);
      } else {
        toast.success('Request rejected.');
      }
      await fetchPending();
    } catch (err) {
      toast.error(err, 'Could not record that decision.');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {issuedToken && (
        <Card>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
              One-time recovery code
            </h3>
            <p style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
              Shown once and stored only as a hash — there is no way to see it again.
              Give it to the customer yourself, on a channel you are satisfied is
              theirs. It expires in 24 hours and works once.
            </p>
            <code
              style={{
                display: 'block',
                padding: 12,
                fontSize: 13,
                wordBreak: 'break-all',
                background: 'var(--neutral-100)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--neutral-800)',
              }}
            >
              {issuedToken}
            </code>
            <div>
              <Button size="sm" onClick={() => setIssuedToken(null)}>
                I've saved it
              </Button>
            </div>
          </div>
        </Card>
      )}

      <Card padding={0}>
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid var(--neutral-300)',
          }}
        >
          <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--neutral-800)' }}>
            Awaiting review
          </h3>
          <p style={{ fontSize: 13, color: 'var(--neutral-500)', marginTop: 2 }}>
            Everyone here has already matched the BVN on their account. Only the photo
            check failed — most recoveries never reach this list.
          </p>
        </div>

        {error && (
          <div
            style={{
              margin: 20,
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        {!error && requests.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--neutral-500)', fontSize: 13 }}>
            Nothing waiting. The automatic checks are settling them.
          </div>
        )}

        {requests.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--neutral-100)' }}>
                  <th style={{ ...cell, textAlign: 'left' }}>Requested</th>
                  <th style={{ ...cell, textAlign: 'left' }}>Identifier</th>
                  <th style={{ ...cell, textAlign: 'left' }}>Evidence</th>
                  <th style={{ ...cell, textAlign: 'left' }}>From</th>
                  <th style={{ ...cell, textAlign: 'left' }}>Note</th>
                  <th style={{ ...cell, textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id}>
                    <td style={cell}>
                      {format(new Date(r.createdAt), 'd MMM, HH:mm')}
                    </td>
                    <td style={cell}>{maskIdentifier(r.identifierUsed)}</td>
                    <td style={cell}>
                      <div style={{ color: 'var(--success-900)' }}>
                        BVN matched
                        {r.identityVerifiedAt
                          ? ` · ${format(new Date(r.identityVerifiedAt), 'HH:mm')}`
                          : ''}
                        {r.attempts > 1 ? ` · after ${r.attempts} tries` : ''}
                      </div>
                      <div style={{ color: 'var(--danger-900)', marginTop: 2 }}>
                        {/* What the model said. The reviewer never saw the
                            attempt, so this is all they have to go on. */}
                        {r.livenessFailure ?? 'Photo check failed'}
                      </div>
                    </td>
                    <td style={cell}>
                      <div>{r.deviceLabel ?? 'Unknown device'}</div>
                      <div style={{ color: 'var(--neutral-500)', marginTop: 2 }}>
                        {r.ipAddress ?? '—'}
                      </div>
                    </td>
                    <td style={cell}>
                      <input
                        value={notes[r.id] ?? ''}
                        onChange={(e) =>
                          setNotes({ ...notes, [r.id]: e.target.value })
                        }
                        placeholder="Why you decided"
                        style={{
                          width: 180,
                          height: 32,
                          padding: '0 8px',
                          fontSize: 12,
                          fontFamily: 'var(--font-family)',
                          color: 'var(--neutral-800)',
                          background: 'var(--neutral-100)',
                          border: '1px solid var(--neutral-300)',
                          borderRadius: 'var(--radius-md)',
                          outline: 'none',
                        }}
                      />
                    </td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busyId === r.id}
                          onClick={() => void decide(r, 'reject')}
                        >
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyId === r.id}
                          onClick={() => void decide(r, 'approve')}
                        >
                          Approve
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};
